import { Text } from '@ardoq/typography';
import { WithPopoverPlacement, WithPopover } from '@ardoq/popovers';
import { IconSize, InfoIcon } from '@ardoq/icons';
import { ColorRangeFormatting, HexColor } from '@ardoq/api-types';
import styled from 'styled-components';
import { colors, space } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { useMemo } from 'react';
import { isNil } from 'lodash';
import { type I18nLocale, t } from '@ardoq/i18n';
import { buildColorScale, CompleteColorRange } from '../buildColorScale';

const ColorRangeContainer = styled(FlexBox).attrs({
  gap: 'small',
  padding: 'small',
})`
  border: 1px solid ${colors.grey300};
  border-radius: 6px;
  height: 34px;
`;

const ColorBlock = styled.div<{ $color: HexColor }>`
  height: 15px;
  width: 15px;
  background-color: ${props => props.$color};
  border-radius: ${space.xxsmall};
`;

const maybePrefillColorRangeForPreview = (
  colorRange: ColorRangeFormatting
): CompleteColorRange => {
  const { min, max } = colorRange;

  const hasMin = !isNil(min.value);
  const hasMax = !isNil(max.value);

  // Case 1: no min and no max
  if (!hasMin && !hasMax) {
    return {
      ...colorRange,
      min: { ...min, value: 1 },
      max: { ...max, value: 3 },
    };
  }

  // Case 2: only min
  if (hasMin && !hasMax) {
    const safeMin = min.value as number;

    return {
      ...colorRange,
      min: { ...min, value: safeMin },
      max: { ...max, value: safeMin + 2 },
    };
  }

  // Case 3: only max
  if (!hasMin && hasMax) {
    const safeMax = max.value as number;

    return {
      ...colorRange,
      min: { ...min, value: safeMax - 2 },
      max: { ...max, value: safeMax },
    };
  }

  // Case 4: both min and max are present
  return {
    ...colorRange,
    min: { ...min, value: min.value as number },
    max: { ...max, value: max.value as number },
  };
};

type Props = {
  colorRange: ColorRangeFormatting;
  i18nLocale: I18nLocale;
};

export const ColorRangePreview = ({ colorRange, i18nLocale }: Props) => {
  const colorsList = useMemo(() => {
    // If rule is incomplete, pre-fill it for preview purposes
    const normalizedColorRange = maybePrefillColorRangeForPreview(colorRange);
    return buildColorScale(normalizedColorRange);
  }, [colorRange]);

  return (
    <FlexBox gap="xsmall" flexDirection="column">
      <FlexBox align="center" gap="xsmall">
        <Text variant="caption" color="textSubtle">
          {t(
            i18nLocale,
            'perspectives:colorRangePreview.title',
            'Color range preview'
          )}
        </Text>
        <WithPopover
          content={t(
            i18nLocale,
            'perspectives:colorRangePreview.popoverContent',
            'These are the colors the components can have. The min, mid and max points determine how the components are distributed across these colors.'
          )}
          preferredPlacement={WithPopoverPlacement.TOP}
        >
          <InfoIcon iconSize={IconSize.SMALL} color={colors.grey500} />
        </WithPopover>
      </FlexBox>
      <ColorRangeContainer>
        <Text variant="caption" color="textSubtle">
          {t(i18nLocale, 'perspectives:colorRangePreview.min', 'min')}
        </Text>
        <FlexBox>
          {colorsList.map((color, index) => (
            <ColorBlock key={`${index}-${color}`} $color={color} />
          ))}
        </FlexBox>
        <Text variant="caption" color="textSubtle">
          {t(i18nLocale, 'perspectives:colorRangePreview.max', 'max')}
        </Text>
      </ColorRangeContainer>
    </FlexBox>
  );
};
